import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import AcceptInvite from './components/AcceptInvite';
import Dashboard from './components/Dashboard';
import UptimeMonitor from './components/UptimeMonitor';
import DNSMonitor from './components/DNSMonitor';
import SSLMonitor from './components/SSLMonitor';
import IPReputation from './components/IPReputation';
import AIAssistant from './components/AIAssistant';
import SystemLogs from './components/SystemLogs';
import UsersManagement from './components/UsersManagement';
import Sidebar from './components/Sidebar';
import api from './api';
import { canAccessPage, firstAllowedRoute } from './pageAccess';
import './App.css';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Refresh role + page access from the server on load, so permission
  // changes show up without relying on what was cached at sign-in.
  useEffect(() => {
    if (!token) return;
    api.get('/auth/me')
      .then(res => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
      })
      .catch(() => {}); // a 401 is handled by the api interceptor
  }, [token]);

  // Route guard: non-admins are sent to their first allowed page.
  const guard = (key, element) =>
    canAccessPage(user, key) ? element : <Navigate to={firstAllowedRoute(user)} replace />;

  const handleLogin = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  if (!token) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />} />
          <Route path="/accept-invite" element={<AcceptInvite darkMode={darkMode} toggleDarkMode={toggleDarkMode} />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public invite page — reachable even while someone is signed in. */}
        <Route path="/accept-invite" element={<AcceptInvite darkMode={darkMode} toggleDarkMode={toggleDarkMode} />} />
        <Route path="*" element={
          <div className="app-container">
            <Sidebar user={user} onLogout={handleLogout} darkMode={darkMode} toggleDarkMode={toggleDarkMode} token={token} />
            <div className="main-content">
              <Routes>
                <Route path="/" element={<Dashboard token={token} user={user} />} />
                <Route path="/uptime" element={guard('uptime', <UptimeMonitor token={token} user={user} />)} />
                <Route path="/dns" element={guard('dns', <DNSMonitor token={token} user={user} />)} />
                <Route path="/ssl" element={guard('ssl', <SSLMonitor token={token} user={user} />)} />
                <Route path="/reputation" element={guard('reputation', <IPReputation user={user} />)} />
                <Route path="/ai" element={guard('ai', <AIAssistant token={token} user={user} />)} />
                <Route path="/logs" element={guard('logs', <SystemLogs token={token} />)} />
                <Route path="/users" element={guard('users', <UsersManagement token={token} user={user} />)} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
